import { AlertColor, GlobalStyles, useTheme } from '@mui/material';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

type SnackbarContextType = {
  showAlert: (message: string, severity?: AlertColor) => void;
};

type ToastEntry = {
  id: string;
  message: string;
  severity: AlertColor;
  exiting: boolean;
};

const ACCENT: Record<AlertColor, string> = {
  error: '#DB5282',
  warning: '#FF9A4D',
  success: '#c0ff02',
  info: '#5AE9FF',
};

const DEFAULT_DURATION = 4000;
const EXIT_ANIMATION_MS = 220;

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const theme = useTheme();

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, EXIT_ANIMATION_MS);
  }, []);

  const showAlert = useCallback(
    (message: string, severity: AlertColor = 'success') => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      setToasts((prev) => [...prev, { id, message, severity, exiting: false }]);
      timers.current[id] = setTimeout(() => dismiss(id), DEFAULT_DURATION);
    },
    [dismiss],
  );

  useEffect(() => {
    const refSnapshot = timers.current;
    return () => {
      Object.values(refSnapshot).forEach(clearTimeout);
    };
  }, []);

  return (
    <SnackbarContext.Provider value={{ showAlert }}>
      {children}
      <GlobalStyles
        styles={{
          '@keyframes hauser-toast-in': {
            from: { opacity: 0, transform: 'translateY(8px) scale(0.97)' },
            to: { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
          '@keyframes hauser-toast-out': {
            from: { opacity: 1, transform: 'scale(1)' },
            to: { opacity: 0, transform: 'scale(0.95)' },
          },
        }}
      />
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            aria-live="polite"
            style={{
              position: 'fixed',
              bottom: 28,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              width: 360,
              zIndex: theme.zIndex.snackbar,
              pointerEvents: 'none',
            }}>
            {[...toasts].reverse().map((t) => (
              <ToastItem key={t.id} entry={t} onDismiss={() => dismiss(t.id)} />
            ))}
          </div>,
          document.body,
        )}
    </SnackbarContext.Provider>
  );
};

type ToastItemProps = {
  entry: ToastEntry;
  onDismiss: () => void;
};

const ToastItem = ({ entry, onDismiss }: ToastItemProps) => {
  const accent = ACCENT[entry.severity] ?? ACCENT.info;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 56,
        background: '#2c2c2c',
        borderRadius: 10,
        paddingLeft: 14,
        paddingRight: 14,
        gap: 14,
        pointerEvents: 'all',
        userSelect: 'none',
        fontFamily: "'Manrope', -apple-system, sans-serif",
        willChange: 'transform, opacity',
        animation: entry.exiting
          ? 'hauser-toast-out 0.2s ease-in forwards'
          : 'hauser-toast-in 0.24s cubic-bezier(0.34,1.18,0.64,1) forwards',
      }}>
      <div
        style={{
          width: 3,
          height: 28,
          borderRadius: 2,
          background: accent,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          flex: 1,
          fontSize: 12,
          fontWeight: 600,
          color: '#efefef',
          letterSpacing: '-0.01em',
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
        {entry.message}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          outline: 'none',
          cursor: 'pointer',
          color: '#555',
          fontSize: 12,
          lineHeight: 1,
          padding: 4,
          flexShrink: 0,
          fontFamily: 'inherit',
          transition: 'color 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#999';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#555';
        }}>
        ✕
      </button>
    </div>
  );
};
